import { useTranslation } from 'react-i18next';
import { activeRowCount } from '~/pages/buy-lists/lib/buyListFilters';
import type { useBuyListFilters } from '~/pages/buy-lists/lib/useBuyListFilters';
import { BuyListTable } from '~/pages/buy-lists/sections/BuyListTable';
import type { BuyListTabProps } from '~/pages/buy-lists/sections/buyListTabProps';
import type { BuyListTeamMember } from '~/pages/buy-lists/sections/BuyListTeamFilter';
import { BuyListToolbar } from '~/pages/buy-lists/sections/BuyListToolbar';

type OrgBuyListProps = BuyListTabProps & {
  readonly filters: ReturnType<typeof useBuyListFilters>;
  readonly buyers: BuyListTeamMember[];
  readonly isLoadingBuyers: boolean;
  readonly hasBuyersFailed: boolean;
};

export function OrgBuyList({
  filters,
  buyers,
  isLoadingBuyers,
  hasBuyersFailed,
  locationOptions,
  vehicleOptions,
  productOptions,
  isLoadingProducts,
  ...tableProps
}: OrgBuyListProps) {
  const { t } = useTranslation('inventory');
  const isTeamFiltered = filters.teamActive && filters.hasTeam;
  const hasRowFilters = activeRowCount(filters.rows) > 0;

  const emptyMessage = () => {
    if (hasRowFilters) return t('inventory.buy_list.filter.filtered_empty');
    if (isTeamFiltered) return t('inventory.buy_list.filter.team_empty_result');
    return t('inventory.buy_list.organization_lists_empty');
  };

  return (
    <div className="mt-6 bg-neut-bg-alt rounded-md flex flex-col gap-2">
      <BuyListToolbar
        buyers={buyers}
        isLoadingBuyers={isLoadingBuyers}
        hasBuyersFailed={hasBuyersFailed}
        isLoadingSavedTeam={filters.isLoading}
        locations={locationOptions}
        transportVehicles={vehicleOptions}
        products={productOptions}
        isLoadingProducts={isLoadingProducts}
        views={filters.views}
        teamMemberIds={filters.teamMemberIds}
        teamActive={filters.teamActive}
        onTeamActiveChange={filters.setTeamActive}
        onSaveTeam={filters.saveTeam}
      />
      <BuyListTable
        {...tableProps}
        loadingMessage={t('inventory.buy_list.organization_lists_loading')}
        emptyMessage={emptyMessage()}
        loadFailedMessage={t(
          'inventory.buy_list.organization_lists_load_failed'
        )}
        testId="buy-list-team-table"
        showBuyerColumn
      />
      {isTeamFiltered &&
        !tableProps.isLoading &&
        !tableProps.hasFailed &&
        tableProps.buyLists.length === 0 && (
          <div className="mt-3 text-center">
            <button
              type="button"
              className="text-sm text-prim hover:underline"
              data-testid="buy-list-show-all-buyers"
              onClick={() => filters.setTeamActive(false)}
            >
              {t('inventory.buy_list.filter.team_show_all')}
            </button>
          </div>
        )}
    </div>
  );
}
